/**
 * Address Component
 *
 * This component renders a form for entering address details including street address, city, country, state, and zip code.
 * It supports dynamic state selection based on the selected country and provides keyboard navigation for state selection.
 *
 * Props:
 * - formData: Object containing the form data.
 * - errors: Object containing validation errors for the form fields.
 * - setFormData: Function to update the form data.
 * - enabled: Boolean to enable or disable the form fields.
 * - displayMode: String to determine the display mode of the form fields ("icon" or "label").
 * - colCount: Number to control the number of columns for the form layout.
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Row, Col, Form, InputGroup } from "react-bootstrap";
import {
  faCity,
  faEnvelope,
  faGlobe,
  faMap,
  faMapMarkerAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import countryStateData from "../../source/countryStateData"; // Adjust the import path as necessary

const Address = ({
  formData,
  errors = {},  // Default to empty object if errors is not provided
  setFormData,
  enabled = true,  // Default to true if enabled is not provided
  displayMode = "icon",  // Default to "icon" if displayMode is not provided
  colCount = 1,  // Default to 1 column if colCount is not provided
  requiredFields = [],  // Default to empty array if requiredFields is not provided
}) => {
  

  const [filteredStates, setFilteredStates] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const timeoutRef = useRef(null);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
      ...(name === "country" && { state: "" }),
    }));

    if (name === "country") {
      setFilteredStates(countryStateData[value] || []);
    }

    if (name === "state" && formData.country) {
      setFilteredStates(
        countryStateData[formData.country].filter((state) =>
          state.toLowerCase().includes(value.toLowerCase())
        )
      );
    }
  };

  const handleKeyDownForState = (event) => {
    if (filteredStates.length === 0) return; // No states to navigate
  
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex < filteredStates.length - 1 ? prevIndex + 1 : prevIndex
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : prevIndex
        );
        break;
      case "Enter":
        event.preventDefault();
        if (highlightedIndex >= 0) {
          handleStateSelect(filteredStates[highlightedIndex]);
        }
        break;
      case "Escape":
        setFilteredStates([]); // Close dropdown on Escape
        break;
      default:
        break;
    }
  };
  

  const handleStateSelect = (state) => {
    setFormData((prevData) => ({ ...prevData, state }));
    setFilteredStates([]); // Clear the list once a state is selected
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      setHighlightedIndex((prevIndex) =>
        prevIndex < filteredStates.length - 1 ? prevIndex + 1 : prevIndex
      );
    } else if (event.key === "ArrowUp") {
      setHighlightedIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : prevIndex
      );
    } else if (event.key === "Enter" && highlightedIndex >= 0) {
      handleStateSelect(filteredStates[highlightedIndex]);
    }
  };

  const handleBlur = () => {
    timeoutRef.current = setTimeout(() => setFilteredStates([]), 100);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const renderCountryOptions = () => {
    return Object.keys(countryStateData).map((countryCode) => (
      <option key={countryCode} value={countryCode}>
        {countryCode}
      </option>
    ));
  };

  const renderField = ({
    label,
    name,
    placeholder,
    type = "text",
    icon,
    isDropdown = false,
    required,
  }) => (
    <Form.Group className="position-relative">
      {displayMode === "label" && (
        <Form.Label htmlFor={name}>
          {label} <span className="text-danger">*</span>
        </Form.Label>
      )}
      <InputGroup>
        {displayMode === "icon" && (
          <InputGroup.Text>
            <FontAwesomeIcon icon={icon} />
            {required && <span className="text-danger">*</span>}
          </InputGroup.Text>
        )}
        {isDropdown ? (
          <Form.Control
            as="select"
            id={name}
            name={name}
            value={formData[name] || ""}
            className={`form-select ${errors[name] ? "is-invalid" : ""}`}
            onChange={handleInputChange}
            disabled={!enabled}
          >
            <option value="">{`Select ${label}`}</option>
            {renderCountryOptions()}
          </Form.Control>
        ) : (
          <Form.Control
            type={type}
            id={name}
            name={name}
            value={formData[name] || ""}
            className={`form-control ${errors[name] ? "is-invalid" : ""}`}
            onChange={handleInputChange}
            onKeyDown={name === "state" ? handleKeyDownForState : undefined}
            onBlur={name === "state" ? handleBlur : undefined}
            placeholder={placeholder}
            autoComplete="off"
            disabled={!enabled}
          />
        )}
      </InputGroup>
      {name === "state" && filteredStates.length > 0 && (
        <ul
          className="list-group position-absolute bg-white border"
          style={{
            zIndex: 1050,
            width: "100%",
            top: "100%",
            left: 0,
            maxHeight: "200px",
            overflowY: "auto",
          }}
          role="listbox"
          aria-expanded={filteredStates.length > 0}
        >
          {filteredStates.map((state, index) => (
            <li
              key={state}
              id={`state-option-${index}`}
              className={`list-group-item list-group-item-action ${
                highlightedIndex === index ? "bg-primary text-white" : ""
              }`}
              onClick={() => handleStateSelect(state)}
              role="option"
              aria-selected={highlightedIndex === index}
              tabIndex="-1"
              onMouseDown={(e) => e.preventDefault()} // Prevents onBlur from triggering prematurely
            >
              {state}
            </li>
          ))}
        </ul>
      )}
      {errors[name] && <span className="text-danger">{errors[name]}</span>}
    </Form.Group>
  );
  

  // Fields array for rendering
  const fields = [
    {
      label: "Street Address",
      name: "address",
      placeholder: "Street Address",
      icon: faMapMarkerAlt,
      required: requiredFields.includes("address"),
    },
    { label: "City", name: "city", placeholder: "City", icon: faCity },
    {
      label: "Country",
      name: "country",
      placeholder: "Country",
      icon: faGlobe,
      isDropdown: true,
      required: requiredFields.includes("country"),
    },
    { label: "State", name: "state", placeholder: "State", icon: faMap,required: requiredFields.includes("state") },
    {
      label: "Zip Code",
      name: "zip",
      placeholder: "Zip Code",
      icon: faEnvelope,
      required: requiredFields.includes("zip"),
    },
  ];

  // Split fields into rows based on colCount
  const rows = [];
  for (let i = 0; i < fields.length; i += colCount) {
    rows.push(fields.slice(i, i + colCount));
  }

  return (
    <div>
      {rows.map((rowFields, rowIndex) => (
        <Row key={rowIndex} className="mb-3">
          {rowFields.map((fieldProps, colIndex) => (
            <Col key={colIndex} md={12 / colCount}>
              {renderField(fieldProps)}

            </Col>
          ))}
        </Row>
      ))}
    </div>
  );
};

Address.propTypes = {
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  enabled: PropTypes.bool,
  displayMode: PropTypes.oneOf(["icon", "label"]),
  colCount: PropTypes.number,  // New prop type for colCount
  errors: PropTypes.object,  // Prop type for errors
  requiredFields: PropTypes.arrayOf(PropTypes.string),  // New prop type for requiredFields
};
export default Address;
